import { ApiError, type ApiErrorBody } from "@/web/lib/api";

function readCookie(name: string) {
  return document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${name}=`))
    ?.split("=")
    .slice(1)
    .join("=");
}

/** Same shape as `api()` but against /api/portal with the portal's own CSRF cookie. */
export async function portalApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !(init.body instanceof Blob) && !headers.has("content-type"))
    headers.set("content-type", "application/json");
  const csrf = readCookie("sailix_portal_csrf");
  if (csrf && !["GET", "HEAD"].includes(init.method ?? "GET")) headers.set("x-csrf-token", decodeURIComponent(csrf));
  const response = await fetch(`/api/portal${path}`, { ...init, headers, credentials: "include" });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
    const code = body.error?.code ?? "request_failed";
    if (response.status === 401 && code === "unauthenticated" && path !== "/me")
      window.dispatchEvent(new CustomEvent("sailix-portal:unauthenticated"));
    throw new ApiError(response.status, code, body.error?.message ?? "The request could not be completed.", body.error ?? {});
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export interface PortalMe {
  member: { email: string; name: string };
  agency: { id: string; name: string; email: string; company: string | null };
  workspace: { name: string; slug: string };
  supportEmail: string | null;
  counts: { open: number; waiting: number; closed: number };
  csrfToken: string;
}

export interface PortalTicketRow {
  id: string;
  number: number;
  subject: string;
  status: string;
  priority: string;
  preview: string;
  messageCount: number;
  lastReplyAt: string | null;
  lastAgentReplyAt: string | null;
  createdAt: string;
  updatedAt: string;
  assigneeName: string | null;
}

export interface PortalThread {
  ticket: {
    id: string;
    number: number;
    subject: string;
    status: string;
    priority: string;
    createdAt: string;
    updatedAt: string;
    lastReplyAt: string | null;
    resolvedAt: string | null;
    closedAt: string | null;
    assigneeName: string | null;
  };
  messages: Array<{
    id: string;
    authorType: "customer" | "agent" | "system";
    authorName: string | null;
    bodyText: string;
    bodyHtml: string | null;
    createdAt: string;
  }>;
  attachments: Array<{ id: string; messageId: string | null; filename: string; contentType: string; size: number }>;
}

/** Two-step upload: ask for a signed intent, then PUT the bytes to it. */
export async function uploadPortalFile(ticketId: string, file: File) {
  const intent = await portalApi<{ upload: { attachmentId: string; url: string } }>("/attachments/intents", {
    method: "POST",
    body: JSON.stringify({ ticketId, filename: file.name, contentType: file.type || "application/octet-stream", size: file.size }),
  });
  const csrf = readCookie("sailix_portal_csrf");
  const response = await fetch(intent.upload.url, {
    method: "PUT",
    credentials: "include",
    headers: {
      "content-type": file.type,
      "content-length": String(file.size),
      ...(csrf ? { "x-csrf-token": decodeURIComponent(csrf) } : {}),
    },
    body: file,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
    throw new ApiError(response.status, body.error?.code ?? "upload_failed", body.error?.message ?? "Upload failed.");
  }
  return intent.upload.attachmentId;
}

export const statusLabel: Record<string, string> = {
  open: "Open",
  pending: "In progress",
  waiting_customer: "Waiting on you",
  resolved: "Resolved",
  closed: "Closed",
};

export const statusTone: Record<string, "neutral" | "green" | "amber" | "red" | "blue" | "violet"> = {
  open: "violet",
  pending: "amber",
  waiting_customer: "blue",
  resolved: "green",
  closed: "neutral",
};

export function formatWhen(value: string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  const diff = Date.now() - date.getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h ago`;
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
